import { mergeRefs } from "@suse-o11y/merge-refs";
import type { Size } from "@suse-o11y/size";
import Konva from "konva";
import React from "react";
import { useMeasure } from "react-use";

import { Container, type ContainerConfig } from "./Container";
import { StageContextProvider } from "./StageContext";

export interface StageProps extends ContainerConfig {
	children: React.ReactNode;
}

export const Stage = React.forwardRef<HTMLDivElement, StageProps>(
	(
		{
			width,
			height,
			x,
			y,
			visible,
			listening,

			onClick,
			onMouseEnter,
			onMouseLeave,
			onMouseDown,
			onMouseMove,
			onMouseUp,

			children,
			...props
		},
		ref,
	) => {
		const containerRef = React.useRef<HTMLDivElement>(null);
		const [measureRef, size] = useMeasure<HTMLDivElement>();
		const config = {
			width: width ?? size.width,
			height: height ?? size.height,
			x,
			y,
			visible,
			listening,
			onClick,
			onMouseEnter,
			onMouseLeave,
			onMouseDown,
			onMouseMove,
			onMouseUp,
		};
		const initialConfigRef = React.useRef(config);
		const [stage, setStage] = React.useState<Konva.Stage>();
		const sizeContext: Size = React.useMemo(
			() => ({
				width: size.width,
				height: size.height,
			}),
			[size.width, size.height],
		);

		React.useLayoutEffect(() => {
			if (containerRef.current != null) {
				setStage(
					new Konva.Stage({
						...initialConfigRef.current,
						container: containerRef.current,
					}),
				);
			}
		}, []);

		return (
			<>
				<div ref={mergeRefs([ref, measureRef, containerRef])} {...props} />
				{stage && (
					<StageContextProvider value={sizeContext}>
						<Container instance={stage} {...config}>
							{children}
						</Container>
					</StageContextProvider>
				)}
			</>
		);
	},
);
